import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertTriangle, Clock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ComplianceScoreCardProps {
  score?: number;
  nextInspectionDays?: number;
  criticalActions?: number;
}

const ComplianceScoreCard = ({ 
  score = 96, 
  nextInspectionDays = 45, 
  criticalActions = 0 
}: ComplianceScoreCardProps) => {
  const navigate = useNavigate();

  // Traffic light system
  const getStatusConfig = () => {
    if (score >= 95) {
      return {
        status: 'COMPLIANT',
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        icon: CheckCircle,
        message: 'All Systems Green'
      };
    } else if (score >= 85) {
      return {
        status: 'ACTION NEEDED',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
        icon: AlertTriangle,
        message: 'Attention Required'
      };
    } else {
      return {
        status: 'CRITICAL',
        color: 'text-red-500',
        bgColor: 'bg-red-500/10',
        icon: AlertTriangle,
        message: 'Audit Risk - Immediate Action'
      };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <Card className="glass-panel-elevated border-2 border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${statusConfig.bgColor}`}>
            <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
          </div>
          <div>
            <div className={`text-xl font-bold ${statusConfig.color}`}>
              {statusConfig.status}
            </div>
            <div className="text-sm text-muted-foreground font-normal">
              {statusConfig.message}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="text-center py-4">
          <div className="text-5xl font-bold mb-2">{score}%</div>
          <div className="text-sm text-muted-foreground">Overall Compliance Score</div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="glass-panel p-4 text-center">
            <Clock className="h-5 w-5 text-blue-500 mx-auto mb-2" />
            <div className="text-2xl font-bold">{nextInspectionDays}</div>
            <div className="text-xs text-muted-foreground">Days Until Inspection</div>
          </div>
          
          <div className="glass-panel p-4 text-center">
            <AlertTriangle className={`h-5 w-5 mx-auto mb-2 ${criticalActions > 0 ? 'text-red-500' : 'text-green-500'}`} />
            <div className="text-2xl font-bold">{criticalActions}</div>
            <div className="text-xs text-muted-foreground">Critical Actions</div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Temperature Logs</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-green-500/20 text-green-500">98%</Badge>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => navigate('/reports')}
                className="h-6 px-2 text-xs"
              >
                View
              </Button>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">HACCP Checklists</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-green-500/20 text-green-500">96%</Badge>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => navigate('/checklists')}
                className="h-6 px-2 text-xs"
              >
                View
              </Button>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Incident Reports</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-500">89%</Badge>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => navigate('/incidents')}
                className="h-6 px-2 text-xs"
              >
                View
              </Button>
            </div>
          </div>
        </div>

        {/* CTA */}
        <Button 
          onClick={() => navigate('/compliance-status')} 
          className="w-full cta-primary"
        >
          <TrendingUp className="mr-2 h-4 w-4" />
          View Detailed Report
        </Button>
      </CardContent>
    </Card>
  );
};

export default ComplianceScoreCard;
